"use client";

import { useState, useEffect } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useRouter, useSearchParams } from "next/navigation";
import { ColumnDef } from "@tanstack/react-table";
import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import { ChevronLeft, FileText, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/shared/data-table";
import { Loader } from "@/components/ui/loader";
import { format } from "date-fns";
import TablePagination from "@/components/shared/table-pagination";
import { SortableColumn } from "@/components/shared/sortable-column";
import { SEARCH_DEBOUNCE_WAIT } from "@/utils/global";

interface DataSourceDetailsProps {
  activeSection?: string;
  setActiveSection?: React.Dispatch<React.SetStateAction<string>>;
  datasource: DataSource;
  onViewGroups: () => void;
}

function getStatusColor(status: string): string {
  switch (status) {
    case "UPLOADING":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "UPLOADED":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "PROCESSED":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
    case "FAILED":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
}

export const DataSourceDetails: React.FC<DataSourceDetailsProps> = ({
  setActiveSection,
  datasource,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get("page") || "1", 10) || 1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (column: string) => {
    setSortBy(column);
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const { data, isLoading, hasPermission } = useProtectedQuery<DocumentFilesResponse>({
    queryKey: ["datasource-documents", datasource.id, debouncedSearch, currentPage, sortBy, sortOrder, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        datasource_id: datasource.id,
        page: currentPage.toString(),
        page_size: "10",
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (statusFilter !== "all") params.set("status", statusFilter);
      return fetchData(`/api/documents/files?${params.toString()}`);
    },
    requiredPermissions: ["LIST_DOCUMENTS"],
  });

  useEffect(() => {
    if (data?.page_count && currentPage > data.page_count) {
      setCurrentPage(1);
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", "1");
      router.replace(`${window.location.pathname}?${params.toString()}`);
    }
  }, [data?.page_count, currentPage, router, searchParams]);

  const columns: ColumnDef<DocumentFile>[] = [
    {
      accessorKey: "name",
      header: () => (
        <SortableColumn label="Name" columnKey="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
      ),
      cell: ({ row }) => (
        <div className="flex items-start gap-3 pl-4">
          <FileText className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="font-medium truncate">{row.getValue("name")}</p>
            {row.original.description && (
              <p className="text-xs text-muted-foreground truncate max-w-[260px]">{row.original.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "type",
      header: () => <div className="inline-flex items-center h-9 px-4">Type</div>,
      cell: ({ row }) => {
        const type = (row.getValue("type") as string)?.toUpperCase();
        return (
          <div className="pl-4">
            <Badge variant="outline" className="font-mono">
              {type || "—"}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: () => <div className="inline-flex items-center h-9 px-4">Status</div>,
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return (
          <div className="pl-4">
            <Badge className={getStatusColor(status)}>
              {status}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: () => (
        <SortableColumn label="Updated At" columnKey="updated_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
      ),
      cell: ({ row }) => {
        const val = row.getValue("updated_at") as string;
        if (!val) return <span className="pl-4 text-muted-foreground">—</span>;
        const date = new Date(val);
        return (
          <div className="flex flex-col pl-4">
            <span>{format(date, "MMM d, yyyy")}</span>
            <span className="text-muted-foreground text-sm">{format(date, "hh:mm a")}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: () => (
        <SortableColumn label="Created At" columnKey="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue("created_at"));
        return (
          <div className="flex flex-col pl-4">
            <span>{format(date, "MMM d, yyyy")}</span>
            <span className="text-muted-foreground text-sm">{format(date, "hh:mm a")}</span>
          </div>
        );
      },
    },
  ];

  if (!hasPermission) {
    return (
      <AccessDeniedCard message="You don't have permission to view documents. Contact your administrator if you need access." />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        {/* Back + title */}
        <button
          type="button"
          onClick={() => setActiveSection?.("list")}
          className="flex w-full items-center gap-2 group sm:w-fit"
        >
          <ChevronLeft className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
          <div className="text-left ml-2 min-w-0">
            <p className="font-semibold text-foreground text-base leading-tight group-hover:text-primary transition-colors truncate">{datasource.name}</p>
            <p className="text-xs text-muted-foreground">
              Last updated {format(new Date(datasource.updated_at), "MMM d, yyyy")}
            </p>
          </div>
        </button>

        {/* Filters */}
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center xl:max-w-xl">
          <Input
            className="w-full min-w-0 sm:flex-1"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startIcon={
              <Search className="h-4 w-4 absolute text-muted-foreground top-1/2 transform -translate-y-1/2" />
            }
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PROCESSED">Processed</SelectItem>
              <SelectItem value="UPLOADED">Uploaded</SelectItem>
              <SelectItem value="UPLOADING">Uploading</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="relative overflow-hidden rounded-lg border bg-card">
        {isLoading && <Loader />}
        <div className="overflow-auto">
          <div className="max-h-[calc(80vh-189px)]">
            <DataTable columns={columns} data={data?.documents ?? []} />
          </div>
        </div>
      </div>

      <TablePagination
        currentPage={currentPage}
        pageCount={data?.page_count || 0}
        onPageChange={setCurrentPage}
        pageParamName="page"
      />
    </div>
  );
};

